import { track } from '@sloyd/analytics';
import { ai } from '@sloyd/api';
import { actions, selectors } from '@sloyd/store-base';
import { toast } from '@sloyd/ui';
import { bridge } from '@sloyd/viewer';
import { call, delay, fork, put, select, takeEvery } from 'saga-ts';
import { $a, beep, guid4, invokeEvent, l } from 'shared-base';
import { parseNog } from '../utils/nog';
import { dataGuard } from './helpers/guards';
import { navToModel } from './helpers/navigation';
import { onCommandError, onCommandSuccess } from './helpers/realtime';

export const saga = {
  id: 'sloyd.ai',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['AI'],
  },
};

type Verb =
  | 'addModel' //
  | 'editModelObject'
  | 'editModelMaterial';

type Action = {
  type: 'AI';
  verb: Verb;
  id: string;
  prompt: string;
  onlySlot?: boolean;
  payload: Json;
};

const map: Record<Verb, any> = {
  addModel: addModel,
  editModelObject: editModelObject,
  editModelMaterial: editModelMaterial,
};

const analyitcsMapPrompt = {
  addModel: 'Add Model with AI',
  addModelError: 'Failed with Add Model with AI',
  editModelObject: 'Edit Model Object with AI',
  editModelObjectError: 'Failed Edit Model Object with AI',
  editModelMaterial: 'Edit Model Material with AI',
  editModelMaterialError: 'Failed Edit Model Material with AI',
};

// NOG
export function* addModel(action: Action) {
  try {
    const { prompt } = action;

    // quality
    // yield fork(qa.bootstrap, action);

    yield put(actions.appState.patch({ isAiRunning: true }));

    const res = yield ai.create(prompt);

    const { data, isSuccess, error } = res;

    // yield fork(qa.onResponse, action, res);

    if (!isSuccess) {
      throw new Error(error);
    }

    let { message, imodel, qualityId }: { message: string; imodel: IModel } = data;
    const { name, id: generatorId, valuesModel, paletteId } = imodel;

    const guid = guid4();

    yield put(
      $a('MODELS', 'create', guid, {
        name,
        generatorId,
        valuesModel,
        paletteId,
      })
    );

    toast.show(message);

    yield delay(150);

    yield navToModel(guid);
    beep();

    yield delay(50);
    yield put(actions.appState.patch({ isAiRunning: false }));
    yield delay(50);
    yield fork(editModelMaterial, { prompt });
  } catch (err: any) {
    // qa.onError(action, err);
    if (typeof err === 'string') {
      toast.error(err);
    }
    track(analyitcsMapPrompt.addModelError, { error: err.message });
    yield put(actions.appState.patch({ isAiRunning: false }));
    invokeEvent('sentry', { err });
  }
}

export function* editModelObject(action: Action) {
  const { id } = action;

  try {
    let { prompt } = action;

    // quality
    // yield fork(qa.bootstrap, action);

    const model = yield* select(selectors.single.$currentModel);
    if (!model) return;
    if (!prompt) {
      prompt = `a modern-looking ${model.name}`;
    }

    const generator = yield* select(selectors.raw.$rawGenerator);
    yield put(actions.appState.patch({ isAiRunning: true }));
    const res = yield ai.edit(prompt, generator);
    yield put(actions.appState.patch({ isAiRunning: false }));

    // yield fork(qa.onResponse, action, res);

    const { data, isSuccess, error } = res;
    console.log('AI EDIT::RES:', res);

    if (!isSuccess) {
      toast.error(error);
      throw new Error(error);
    }

    const { message, valuesModel, qualityId } = data;

    toast.show(message);
    // don't apply to realtime commands (with id)
    // if (message && !id) {
    //   yield put(actions.appState.patch({ aiExplanation: message }));
    // }

    yield delay(150);

    console.log('AI EDIT::valuesModel:', valuesModel);
    yield put(actions.valuesModel.patch(valuesModel));
    bridge.patchValuesModel('main', valuesModel);

    yield call(onCommandSuccess, id);
    beep();
  } catch (err: any) {
    // yield fork(qa.onError, action, err);
    track(analyitcsMapPrompt.editModelObjectError, { error: err.message });
    yield put(actions.appState.patch({ isAiRunning: false }));
    invokeEvent('sentry', { err });
    yield call(onCommandError, id, err);
  }
}

export function* editModelMaterial(action: Action) {
  const { id } = action;

  try {
    let { prompt, onlySlot } = action;

    // quality
    // yield fork(qa.bootstrap, action);

    const model = yield* select(selectors.single.$currentModel);
    const allMaterials = yield* select(selectors.raw.$rawAllMaterials);
    const settings = yield* select(selectors.raw.$rawSettings);
    const { slotId } = yield* select(selectors.raw.$rawCurrentIds);

    if (!model) return;

    if (!prompt) {
      prompt = `a beautiful ${model.name}`;
    }

    const { generatorId } = model;

    let res: any;

    if (!generatorId) {
      throw new Error('No generatorId');
    }

    yield put(actions.appState.patch({ isAiRunning: true }));

    res = yield ai.texturize(prompt, generatorId, {
      allMaterials,
      textResolution: settings.texturesResolution,
    });

    // yield fork(qa.onResponse, action, res);

    if (!res) {
      throw new Error('No for response texturing');
    }

    yield put(actions.appState.patch({ isAiRunning: false }));

    const { gpt_message, isSuccess, error } = res;

    if (!res || !isSuccess) {
      toast.error(error);
      throw new Error(error);
    }

    toast.show(gpt_message);
    beep();

    let pack = res.data;

    if (onlySlot) {
      pack = {
        [slotId]: pack[slotId],
      };
    }

    bridge.patchMaterials('main', pack);

    yield put(actions.models.patch(model.id, { materials: pack }));
    yield call(onCommandSuccess, id);
  } catch (err: any) {
    track(analyitcsMapPrompt.editModelMaterialError, { error: err.message });
    yield put(actions.appState.patch({ isAiRunning: false }));
    invokeEvent('sentry', { err });
    yield call(onCommandError, id, err);
  }
}

export function* aiPrompt(action: any) {
  let { id, verb, origin, prompt, payload } = action;
  const editMode = yield* select(selectors.base.$editMode);
  const focusMode = yield* select(selectors.base.$focusMode);

  if (!verb) {
    if (focusMode === 'spawn') {
      verb = 'addModel';
    } else if (editMode === 'object') {
      verb = 'editModelObject';
    } else if (editMode === 'material') {
      verb = 'editModelMaterial';
    }

    action.verb = verb;
  }

  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  l(`saga|saga.ai ${verb} on ${id}`, { prompt }, true);
  track((analyitcsMapPrompt as any)[verb], { origin, id, prompt });

  yield* dataGuard();

  yield* fork(saga, action);
}

export function* root() {
  yield takeEvery('PROMPT', aiPrompt);
}
